import { css } from "@emotion/css";
import type { PageAction, PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { Callout, ListItemLink, ListItemContent, Pagination, SearchInput, List, EmptyStateNoResults } from "@octopusdeploy/design-system-components";
import { borderRadius, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { Repository, ResourceCollection, TagSetResource, CustomFieldScopes, CustomFieldResource } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import { links, type QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { sortBy } from "lodash";
import { useCallback, useEffect, useState } from "react";
import * as React from "react";
import useIsMultiTenancyEnabledFeatureFlag from "~/areas/configuration/hooks/useIsMultiTenancyEnabledFeatureFlag";
import { AdvancedFilters } from "~/components/AdvancedFilterLayout/AdvancedFilters";
import Dialog from "~/components/Dialog/Dialog";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import { PortalEmptyStateOnboarding } from "~/components/GettingStarted/PortalEmptyStateOnboarding";
import MarkdownDescription from "~/components/MarkdownDescription";
import { CustomFieldScopeMultiSelect, SupportedCustomFieldScopes } from "~/components/MultiSelect/CustomFieldScopeMultiSelect";
import ExternalLink from "~/components/Navigation/ExternalLink/index";
import InternalLink from "~/components/Navigation/InternalLink/InternalLink";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import Tag from "~/components/Tag/index";
import { useDebouncedExternalState } from "~/hooks/useDebouncedExternalState";
import { DataTable } from "~/primitiveComponents/dataDisplay/DataTable/DataTable";
import { DataTableBody } from "~/primitiveComponents/dataDisplay/DataTable/DataTableBody";
import { DataTableHeader } from "~/primitiveComponents/dataDisplay/DataTable/DataTableHeader";
import { DataTableHeaderColumn } from "~/primitiveComponents/dataDisplay/DataTable/DataTableHeaderColumn";
import { DataTableRow } from "~/primitiveComponents/dataDisplay/DataTable/DataTableRow";
import { DataTableRowColumn } from "~/primitiveComponents/dataDisplay/DataTable/DataTableRowColumn";
import ListTitle from "~/primitiveComponents/dataDisplay/ListTitle";
import { resolveTagSetRepository } from "~/utils/customFieldHelper";
import TagSetsSorter from "./TagSetsSorter";
import styles from "./style.module.less";
export interface TenantTagSetsOrCustomFieldsQueryParams {
    tagSetName: string;
    scopes?: string[];
    page: number | undefined;
    pageSize: number | undefined;
}
export interface TenantTagSetsOrCustomFieldsProps {
    spaceId: string;
    queryParams: TenantTagSetsOrCustomFieldsQueryParams;
    setQueryParams: QueryParamValuesSetter<TenantTagSetsOrCustomFieldsQueryParams>;
    loaderData: TenantTagSetsOrCustomFieldsLoaderData;
}
interface Filter extends TenantTagSetsOrCustomFieldsQueryParams {
}
interface TenantTagSetsOrCustomFieldsLoaderData {
    tagSets: ResourceCollection<TagSetResource>;
    hasTagSets: boolean;
}
const getPageTitle = (isExtendedTagSetsEnabled: boolean) => (isExtendedTagSetsEnabled ? "Custom Fields" : "Tenant Tag Sets");
const defaultPageSize = 30;
const pageSizeOptions = [defaultPageSize, 50, 100];
const localStorageKeyForDismissedCallout = "Octopus.Callout.TagSets.Dismissed";
const defaultTagSetSummaryFilter: Filter = {
    tagSetName: "",
    scopes: [],
    page: 1,
    pageSize: defaultPageSize,
};
export async function tenantTagSetsPageLoader(repository: Repository, queryParams: TenantTagSetsOrCustomFieldsQueryParams): Promise<TenantTagSetsOrCustomFieldsLoaderData> {
    return await getPaginatedTagSets(repository, queryParams);
}
export function TenantTagSetsOrCustomFields({ spaceId, queryParams, setQueryParams, loaderData }: TenantTagSetsOrCustomFieldsProps) {
    const navigation = useSpaceAwareNavigation();
    const isMultiTenancyEnabled = useIsMultiTenancyEnabledFeatureFlag();
    const isExtendedTagSetsEnabled = isFeatureToggleEnabled("ExtendedTagSetsFeatureToggle");
    const sortDialogControls = useDialogTrigger();
    const [debouncedVariableSetName, setDebouncedVariableSetName] = useDebouncedExternalState(queryParams.tagSetName, (value) => setQueryParams({ tagSetName: value, page: 1 }), 200);
    const { result: pagedTagSetsQueryResult, refetch: reloadTagSets } = usePagedTagSetsQuery(queryParams, loaderData);
    const { tagSets, hasTagSets } = pagedTagSetsQueryResult;
    const isFiltered = isFiltering(queryParams);
    const onPageChange = useCallback((page: number) => {
        setQueryParams({ ...queryParams, page });
    }, [queryParams, setQueryParams]);
    const onPageSizeChange = useCallback((pageSize: number) => {
        setQueryParams({ ...queryParams, pageSize, page: 1 });
    }, [queryParams, setQueryParams]);
    useEffect(() => {
        if (!isMultiTenancyEnabled) {
            navigation.redirect(links.tenantsPage.generateUrl({ spaceId }));
        }
    }, [isMultiTenancyEnabled, navigation, spaceId]);
    const [calloutDismissed, setCalloutDismissed] = useState<boolean>(localStorage.getItem(localStorageKeyForDismissedCallout) === "true");
    const addTagSetPageAction: PrimaryPageAction = {
        type: "navigate",
        hasPermissions: isAllowed({ permission: Permission.TagSetCreate }),
        label: isExtendedTagSetsEnabled ? "Add Custom Field" : "Create Tag Set",
        path: isExtendedTagSetsEnabled ? links.createCustomFieldPage.generateUrl({ spaceId }) : links.createTenantTagSetPage.generateUrl({ spaceId }),
    };
    if (!hasTagSets) {
        return (<PortalEmptyStateOnboarding title={isExtendedTagSetsEnabled ? "Create custom fields to easily classify and group tenants" : "Create tenant tags to easily classify and group tenants"} primaryAction={addTagSetPageAction} description={isExtendedTagSetsEnabled
                ? "Custom fields help you to classify your tenants using fields that meet your needs, and tailor tenanted deployments for your projects and environments."
                : "Tenant tags help you to classify your tenants using custom tags that meet your needs, and tailor tenanted deployments for your projects and environments."} learnMore={<ExternalLink href="MultiTenantTags">Learn more</ExternalLink>}/>);
    }
    const customFieldScopes = SupportedCustomFieldScopes.map((scope) => ({ Id: scope, Name: scope }));
    const hideBottomPagination = tagSets.Items.length < 20;
    const advancedFilters = (<AdvancedFilters>
            <CustomFieldScopeMultiSelect value={queryParams.scopes ?? []} label="Custom field scopes" items={customFieldScopes} onChange={(scopes) => setQueryParams({ ...queryParams, scopes })}/>
        </AdvancedFilters>);
    const filters = {
        inputs: [
            <SearchInput value={debouncedVariableSetName} onChange={setDebouncedVariableSetName} accessibleName={isExtendedTagSetsEnabled ? "Search by Custom Field name" : "Search by tag set name"} placeholder={isExtendedTagSetsEnabled ? "Filter by Custom Field name..." : "Filter by tag set name..."}/>,
        ],
        advancedFilters: isExtendedTagSetsEnabled && customFieldScopes.length > 1
            ? {
                content: advancedFilters,
                onResetFilter: () => {
                    setQueryParams(defaultTagSetSummaryFilter);
                },
                hasUserSelectedValues: isFiltered,
            }
            : undefined,
    };
    return (<PageContent pagination={{
            ui: (<Pagination label={isExtendedTagSetsEnabled ? "Custom Fields" : "Tenant Tag Sets"} totalResults={tagSets.TotalResults} itemsPerPageOptions={pageSizeOptions} selectedItemsPerPage={tagSets.ItemsPerPage} currentPage={queryParams.page ?? 1} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange}/>),
            placement: hideBottomPagination ? "top" : "topAndBottom",
        }} header={{ title: getPageTitle(isExtendedTagSetsEnabled), primaryAction: addTagSetPageAction, pageActions: getPageActions(sortDialogControls.openDialog) }} filters={filters}>
            <>
                {calloutDismissed ? (<></>) : (<div style={{ marginBottom: 10 }}>
                        <Callout type={"information"} title="Tenant Tag Sets are expanding" canClose={true} onClose={() => onCloseCallout(setCalloutDismissed)}>
                            <div>
                                The Tenant Tag Sets feature is expanding. This update provides greater flexibility, allowing you to define and capture values tailored to your deployment needs. We'd love to hear{" "}
                                <ExternalLink href={"field-sets-survey"}>your feedback</ExternalLink>.
                            </div>
                        </Callout>
                    </div>)}

                <ContentBorder>
                    <Dialog open={sortDialogControls.isOpen}>
                        <TagSetsSorter onSaved={reloadTagSets}/>
                    </Dialog>
                    {isExtendedTagSetsEnabled ? (tagSets.Items.length > 0 ? (<CustomFieldsTable customFields={tagSets.Items} spaceId={spaceId}/>) : (<EmptyStateNoResults />)) : (<List items={tagSets.Items} renderRow={({ item }) => <TagSetListItem spaceId={spaceId} tagSet={item}/>} rowKey={(item) => item.Id} empty={<EmptyStateNoResults />}/>)}
                </ContentBorder>
            </>
        </PageContent>);
}
function onCloseCallout(setCalloutDismissed: (setCalloutDismissed: ((prevState: boolean) => boolean) | boolean) => void) {
    localStorage.setItem(localStorageKeyForDismissedCallout, true.toString());
    setCalloutDismissed(true);
}
interface GetPaginatedTagSetsOptions {
    page: number | undefined;
    pageSize: number | undefined;
    tagSetName: string | undefined;
    scopes?: string[];
}
async function getPaginatedTagSets(repository: Repository, { page = 1, pageSize = defaultPageSize, tagSetName = "", scopes = [] }: GetPaginatedTagSetsOptions) {
    const tagSetRepository = resolveTagSetRepository(repository);
    const tagSets = tagSetRepository.list({
        skip: (page - 1) * pageSize,
        take: pageSize,
        partialName: tagSetName,
        scopes: scopes.length > 0 ? scopes : undefined,
    });
    const tagSetsStats = tagSetRepository.list({ skip: 0, take: 0 });
    return {
        tagSets: await tagSets,
        hasTagSets: (await tagSetsStats).TotalResults > 0,
    };
}
function usePagedTagSetsQuery(queryParams: TenantTagSetsOrCustomFieldsQueryParams, loaderData: TenantTagSetsOrCustomFieldsLoaderData) {
    return useQuery((repository) => getPaginatedTagSets(repository, { page: queryParams.page, pageSize: queryParams.pageSize, tagSetName: queryParams.tagSetName, scopes: queryParams.scopes }), [queryParams.page, queryParams.pageSize, queryParams.tagSetName, queryParams.scopes], "Get paginated tag sets", {
        initialResult: loaderData,
    });
}
function isFiltering(queryParams: TenantTagSetsOrCustomFieldsQueryParams): boolean {
    return queryParams.tagSetName !== "" || (queryParams.scopes?.length ?? 0) > 0;
}
function getPageActions(openReorderDialogClick: () => void): PageAction[] {
    const sortDialogPageAction: PageAction = {
        type: "button",
        buttonType: "secondary",
        hasPermissions: isAllowed({ permission: Permission.TagSetCreate }),
        label: "Reorder",
        onClick: openReorderDialogClick,
    };
    return [sortDialogPageAction];
}
function TagSetListItem({ tagSet, spaceId }: {
    tagSet: TagSetResource;
    spaceId: string;
}) {
    return (<ListItemLink href={links.tenantTagSetPage.generateUrl({ spaceId, tagSetId: tagSet.Id })}>
            <ListItemContent>
                <ListTitle>{tagSet.Name}</ListTitle>
                <div className={styles.helpText}>
                    <MarkdownDescription markup={tagSet.Description}/>
                </div>
                <div>
                    {sortBy(tagSet.Tags, (t) => t.SortOrder).map((t) => (<Tag tagName={t.Name} tagColor={t.Color} key={t.Id} description={t.Description} borderColor={themeTokens.color.border.primary}/>))}
                </div>
            </ListItemContent>
        </ListItemLink>);
}
function CustomFieldsTable({ customFields, spaceId }: {
    customFields: CustomFieldResource[];
    spaceId: string;
}) {
    const isExtendedTagSetsEnabled = isFeatureToggleEnabled("ExtendedTagSetsFeatureToggle");
    const editLinkGenerator = isExtendedTagSetsEnabled ? (tagSetId: string) => links.editCustomFieldPage.generateUrl({ spaceId, customFieldId: tagSetId }) : (tagSetId: string) => links.tenantTagSetPage.generateUrl({ spaceId, tagSetId });
    const formatScope = (scope?: CustomFieldScopes[]) => {
        if (!scope || scope.length === 0) {
            return "Not specified";
        }
        return scope.join(", ");
    };
    return (<DataTable>
            <DataTableHeader>
                <DataTableRow>
                    <DataTableHeaderColumn>Name</DataTableHeaderColumn>
                    <DataTableHeaderColumn>Scope</DataTableHeaderColumn>
                    <DataTableHeaderColumn>Type</DataTableHeaderColumn>
                </DataTableRow>
            </DataTableHeader>
            <DataTableBody>
                {customFields.map((customField) => (<DataTableRow key={customField.Id}>
                        <DataTableRowColumn>
                            <InternalLink to={editLinkGenerator(customField.Id)}>{customField.Name}</InternalLink>
                        </DataTableRowColumn>
                        <DataTableRowColumn>{formatScope(customField.Scopes)}</DataTableRowColumn>
                        <DataTableRowColumn>{customField.Type || "Not specified"}</DataTableRowColumn>
                    </DataTableRow>))}
            </DataTableBody>
        </DataTable>);
}
function ContentBorder({ children }: {
    children: React.ReactNode;
}) {
    return <div className={contentBorderStyles}>{children}</div>;
}
const contentBorderStyles = css({
    border: `1px solid ${themeTokens.color.border.primary}`,
    borderRadius: borderRadius.medium,
});
